import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSignal } from '@preact/signals-react';
import { toast } from 'react-toastify';
import PageForm from '../../templates/PageForm';
import CopyPasteTable, { HeaderTable } from '../../organisms/CopyPasteTable';
import SelectFilter from '../../organisms/SelectFilter';
import TextEdit from '../../molecules/TextEdit';
import useFetch from '../../../hooks/useFetch';
import appConfig from '../../../config/app-config';
import tokenAwareFetcher from '../../../utils/tokenAwareFetcher';

const DATA_PROVIDER_ENV = appConfig.apis.dataProviderApiEnv;

const URL = `${DATA_PROVIDER_ENV.address}/batch-reports/page-form`;
const API_KEY = DATA_PROVIDER_ENV.key;

const fetcher = tokenAwareFetcher(DATA_PROVIDER_ENV.address, API_KEY);

interface Form {
    identification: string;
    documents: string[];
}

const BatchReportPageForm: React.FC = () => {
    const navigate = useNavigate();
    const form = useSignal<Form>({ identification: '', documents: [] });
    const reportType = useSignal('');
    const isSending = useSignal(false);
    const buttonDisabled = useSignal(true);
    const hasErrors = useSignal(false);
    const apiVersion = useSignal('');
    const headers = useSignal<HeaderTable[]>([
        { label: 'CNPJ', field: 'cnpj', required: true, format: 'govTaxId' },
    ]);

    interface Provider {
        id: string;
        name: string;
        description: string;
        documentType: string;
    }

    const reportTypes = useSignal<Provider[]>([]);

    const { isLoading, data, error, isTokenError } = useFetch(`${URL}`, [{ key: 'x-api-key', value: API_KEY }]);

    if (isTokenError) {
        navigate("/denied");
    }

    if (error) {
        navigate("/network-error");
    }

    useEffect(() => {
        if (data) {
            apiVersion.value = `${data.apiName} - ${data.apiVersion}`;

            reportTypes.value = data.reportTypes;
        }
    }, [data]); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        const handleJson = (event: CustomEvent) => {
            const data = event.detail as Array<{ document: string }>;
            form.value = { ...form.value, documents: data.map(item => item.document) };
            buttonDisabled.value = hasErrors.value || data.length === 0 || !form.value.identification.trim();
        };
        window.addEventListener('CopyPasteTable:jsonGenerated', handleJson as EventListener);
        return () => window.removeEventListener('CopyPasteTable:jsonGenerated', handleJson as EventListener);
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        const handleErrors = (event: CustomEvent) => {
            const errors = event.detail as unknown[];
            hasErrors.value = errors.length > 0;
            buttonDisabled.value = hasErrors.value || form.value.documents.length === 0 || !form.value.identification.trim();
        };
        window.addEventListener('CopyPasteTable:errors', handleErrors as EventListener);
        return () => window.removeEventListener('CopyPasteTable:errors', handleErrors as EventListener);
    }, [form.value.documents.length]); // eslint-disable-line react-hooks/exhaustive-deps

    const submitForm = async () => {
        if (buttonDisabled.value) {
            toast.error('Por favor, corrija os erros antes de enviar o formulário.');
            return;
        }

        isSending.value = true;
        try {
            const response = await fetcher.post('/batch-reports', {
                reportTypeId: reportType.value,
                identification: form.value.identification,
                documents: form.value.documents,
            });
            if (response.ok) {
                const data = await response.json();
                toast.info('Arquivo enviado para processamento.');
                navigate(`/data-provider/batch-report/view/${data.batchId}/0/100/document/ASC`, {
                    state: { fromForm: true }
                });
            } else {
                const { message } = await response.json();
                toast.error(message || 'Erro ao enviar formulário.');
            }
        } catch (err) {
            toast.error('Erro de rede ao enviar formulário.');
        }
        isSending.value = false;
    };

    const cancelForm = () => navigate(-1);

    const clearData = () => {
        window.dispatchEvent(new Event('CopyPasteTable:clearData'));
    };

    return (
        <PageForm
            pageTitle='No relatório de dados em lote'
            apiVersion={apiVersion.value}
            isLoading={isLoading}
            breadcrumb={[{ label: 'Relatórios em lote', url: '/data-provider/batch-report/0/100/createdAt/DESC' }, { label: 'Novo lote', url: '#' }]}
            controlSlot={(
                <div className='flex space-x-4 justify-end m-2'>
                    {reportType.value && (
                        <button
                            className='bg-red-500 text-white px-4 py-2 rounded hover:bg-red-700'
                            onClick={clearData}
                            disabled={isLoading || isSending.value}
                        >
                            Limpar Dados
                        </button>
                    )}
                    <button
                        className='text-black bg-slate-200 px-4 py-2 hover:bg-slate-400 rounded'
                        onClick={cancelForm}
                        disabled={isLoading || isSending.value}
                    >
                        Cancelar
                    </button>
                    <button
                        className='bg-blue-500 text-white px-4 py-2 rounded hover:bg-blue-800 disabled:opacity-50 disabled:cursor-not-allowed'
                        onClick={submitForm}
                        disabled={isLoading || isSending.value || buttonDisabled.value}
                    >
                        Salvar
                    </button>
                </div>
            )}
        >
            <div className='container mx-auto p-4 space-y-4'>

                <SelectFilter
                    id='slc-report-type'
                    label='Tipo de relatório:'
                    value={reportType.value}
                    onChange={(value) => {
                        reportType.value = value;
                        const selectedProvider = reportTypes.value.find((p: any) => p.id === value);
                        const documentType = selectedProvider?.documentType;
                        headers.value = [{
                            label: documentType === 'ALL' ? 'CPF/CNPJ' : documentType === 'GOV_TAX_ID' ? 'CNPJ' : 'CPF',
                            field: 'document',
                            required: true,
                            format: documentType === 'ALL' ? 'taxDocument' : documentType === 'GOV_TAX_ID' ? 'govTaxId' : 'socialId',
                        }];
                    }}
                    defaultVoidOption
                    defaultLabel='Selecione o provedor de dados....'
                    options={reportTypes.value.map(({ id, description, name }: { id: string; description: string; name: string }) => ({
                        value: id,
                        label: `(${name}) - ${description}`,
                    }))}
                />
                <div className='space-y-1'>
                    <TextEdit
                        label='Identificação do Lote'
                        value={form.value.identification}
                        onChange={(e) => {
                            const value = e.target.value;
                            form.value = { ...form.value, identification: value };
                            buttonDisabled.value = hasErrors.value || form.value.documents.length === 0 || !value.trim();
                        }}
                        maxLength={180}
                        required
                    />
                    <p className='text-xs text-gray-500'>
                        {form.value.identification.length}/180 caracteres
                    </p>
                </div>
                {reportType.value && (
                    <CopyPasteTable key={reportType.value} fixedHeaders={headers.value} />
                )}
            </div>
        </PageForm>
    );
};

export default BatchReportPageForm;
